import {
  Button,
  Popover,
  ActionList,
  Card,
  TextStyle,
  TextField
} from '@superops/ui-design-system';
import React, { useState } from 'react';
import { SVGIcon } from '@superops/super-icons';
import {
  AI_AGENTS_CONSTANT_FEATURE_ID_MAP,
  getTranslatedText,
  withErrorBoundary
} from '@nile/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { ADD_AI_AGENT_FEEDBACK } from '@nile/banyan/src/Graphql/Mutations/ai';
import { AccountClient } from '@nile/graphql';

const PopoverContent = (props) => {
  const {
    togglePopover,
    defaultFeedbackType,
    getSuggestedFields,
    ticketId,
    onFeedbackComplete
  } = props;

  const [feedbackComplete, setFeedbackComplete] = useState(false);
  const intl = useIntl();
  const [selectedFeedbackType, setSelectedFeedbackType] = useState(
    defaultFeedbackType
  );
  const [feedbackText, setFeedbackText] = useState('');

  const [addFeedback] = useMutation(ADD_AI_AGENT_FEEDBACK, {
    client: AccountClient
  });

  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const sendFeedback = async () => {
    const feedbackComment = feedbackText;

    let data;
    if (
      getSuggestedFields &&
      selectedFeedbackType.includes('SUGGESTED_FIELDS')
    ) {
      data = { data: getSuggestedFields() };
    }

    try {
      setFeedbackLoading(true);
      const response = await addFeedback({
        variables: {
          input: {
            aiAgent: {
              featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
            },
            sourceAction: selectedFeedbackType,
            message: feedbackComment,
            entity: 'Ticket',
            entityId: ticketId,
            data
          }
        }
      });
      if (response?.data?.addAIAgentFeedback) {
        setFeedbackComplete(true);
        setTimeout(() => {
          onFeedbackComplete({
            ticketId: ticketId,
            feedbackType: selectedFeedbackType,
            isDisliked: !!defaultFeedbackType || selectedFeedbackType.length > 1
          });
        }, 500);
      }
    } catch (error) {
    } finally {
      setFeedbackLoading(false);
    }
  };

  const onSend = () => {
    sendFeedback();
  };

  if (!selectedFeedbackType) {
    return (
      <ActionList
        actionRole="menuitem"
        items={[
          {
            content: 'Field suggestion',
            onAction: () => setSelectedFeedbackType(['SUGGESTED_FIELDS'])
          },
          {
            content: getTranslatedText(intl, 'common.suggested.email'),
            onAction: () =>
              setSelectedFeedbackType(['SUGGESTION_CLARIFICATION'])
          },
          {
            content: 'Both',
            onAction: () =>
              setSelectedFeedbackType([
                'SUGGESTED_FIELDS',
                'SUGGESTION_CLARIFICATION'
              ])
          }
        ]}
      />
    );
  }

  if (feedbackComplete) {
    return (
      <Card padding="sizeSmall" noShadow padding="none">
        <div className="fx g10 col p16 ai-triage-feedback-popover actr jctr tcenter">
          <SVGIcon name="SuccessRound" size="xl" />
          <TextStyle variation="subdued">
            <FormattedMessage id="ticket.sideConversation.feedback.done" />
          </TextStyle>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="sizeSmall" noShadow padding="none">
      <div className="fx g10 col p16 ai-triage-feedback-popover">
        <>
          <div className="fx">
            <TextStyle>
              <FormattedMessage id="ticket.sideConversation.feedback.title" />
            </TextStyle>
            <div onClick={togglePopover} className="mlauto pointer">
              <SVGIcon name="Close" />
            </div>
          </div>
          <div className="content">
            <TextField
              value={feedbackText}
              onChange={(val) => {
                setFeedbackText(val);
              }}
              placeholder={getTranslatedText(
                intl,
                'ticket.sideConversation.feedback.placeholder'
              )}
              type="text"
              bgTransparent
              multiline
              maxLength={255}
              maxHeight={80}
            />
          </div>
          <div className="mlauto w-fit">
            <Button
              disabled={!feedbackText}
              plain
              onClick={sendFeedback}
              loading={feedbackLoading}
            >
              <FormattedMessage id="common.send" />
            </Button>
          </div>
        </>
      </div>
    </Card>
  );
};

const TriageFeedbackWithPopup = (props) => {
  const {
    defaultFeedbackType,
    getSuggestedFields,
    ticketId,
    onComplete
  } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const togglePopover = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  const onFeedbackComplete = (feedbackData) => {
    setIsPopoverOpen(false);
    if (onComplete) {
      onComplete(feedbackData);
    }
  };

  return (
    <Popover
      activator={
        <Button plainSecondary onClick={togglePopover}>
          <SVGIcon name="ThumbsDownOutline" color="#fff" />
        </Button>
      }
      active={isPopoverOpen}
      onClose={() => {}}
      preferredPosition="above"
    >
      <PopoverContent
        togglePopover={togglePopover}
        defaultFeedbackType={defaultFeedbackType}
        getSuggestedFields={getSuggestedFields}
        ticketId={ticketId}
        onFeedbackComplete={onFeedbackComplete}
      />
    </Popover>
  );
};

export default withErrorBoundary(TriageFeedbackWithPopup);
